import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  TRIAL_DAYS, SALES_EMAIL, formatCAD,
} from '@/lib/billing';
import type { SubscriptionPlan } from '@/lib/catalog';
import { findPlan, planPrice } from '@/lib/catalog';
import type { SubscriptionTier } from '@/lib/types';

/**
 * PricingComparison — shared side-by-side feature matrix.
 *
 * Used by:
 *  - /pricing (variant='public') → CTAs route to sign-up / contact sales
 *  - /billing (variant='billing') → CTAs disabled (Stripe not wired)
 *      and the current tier column gets a "Current plan" badge.
 *
 * Plans are passed in (fetched by parent from DB-backed catalog).
 */

type Cell = boolean | 'soon' | string;

interface FeatureRow {
  label: string;
  hint?: string;
  standard: Cell;
  pro: Cell;
  enterprise: Cell;
}

interface FeatureGroup {
  group: string;
  rows: FeatureRow[];
}

const FEATURE_MATRIX: FeatureGroup[] = [
  {
    group: 'Scheduling & operations',
    rows: [
      { label: 'Monthly schedule with drag-and-drop shifts', standard: true, pro: true, enterprise: true },
      { label: 'Copy a previous month as a starting point', standard: true, pro: true, enterprise: true },
      { label: 'Time-off requests with approval flow', standard: true, pro: true, enterprise: true },
      { label: 'Print-ready single-page month view', standard: true, pro: true, enterprise: true },
      { label: 'Append-only audit log', hint: 'Every change captured for compliance.', standard: true, pro: true, enterprise: true },
      { label: 'Staff per location', standard: 'Up to 25', pro: 'Up to 25', enterprise: 'Custom' },
    ],
  },
  {
    group: 'Multi-location',
    rows: [
      { label: 'Single-location dashboard', standard: true, pro: true, enterprise: true },
      { label: 'Multi-location consolidated view', hint: 'See every pharmacy on one screen.', standard: false, pro: 'soon', enterprise: 'soon' },
      { label: 'Cross-location staff reassignment', standard: false, pro: 'soon', enterprise: 'soon' },
    ],
  },
  {
    group: 'Communications & integrations',
    rows: [
      { label: 'In-app communication banner', standard: true, pro: true, enterprise: true },
      { label: 'SMS shift reminders', standard: false, pro: 'soon', enterprise: 'soon' },
      { label: 'Calendar export — Google / Outlook', standard: false, pro: 'soon', enterprise: 'soon' },
      { label: 'Payroll & POS integrations', standard: false, pro: false, enterprise: 'soon' },
    ],
  },
  {
    group: 'Support & onboarding',
    rows: [
      { label: 'Self-serve onboarding wizard', standard: true, pro: true, enterprise: true },
      { label: 'Email support', standard: 'Next business day', pro: 'Same business day', enterprise: 'Same business day' },
      { label: 'Priority support queue', standard: false, pro: true, enterprise: true },
      { label: 'Dedicated account manager', standard: false, pro: false, enterprise: true },
      { label: 'Custom onboarding & data migration', standard: false, pro: false, enterprise: true },
      { label: 'Service Level Agreement (SLA)', standard: false, pro: false, enterprise: true },
    ],
  },
  {
    group: 'Compliance & infrastructure',
    rows: [
      { label: 'Canadian hosting (ca-central-1)', standard: true, pro: true, enterprise: true },
      { label: 'PIPEDA-aligned Data Processing Agreement', standard: true, pro: true, enterprise: true },
      { label: 'Daily encrypted backups', standard: true, pro: true, enterprise: true },
      { label: 'Volume pricing on annual contract', standard: false, pro: false, enterprise: true },
    ],
  },
];

export interface PricingComparisonProps {
  plans: SubscriptionPlan[];
  period: 'monthly' | 'annual';
  variant: 'public' | 'billing';
  currentTier?: SubscriptionTier | null;
}

export function PricingComparison({ plans, period, variant, currentTier }: PricingComparisonProps) {
  const priceLine = (tier: SubscriptionTier) => {
    const plan = findPlan(plans, tier);
    if (!plan) return '—';
    const p = planPrice(plan, period);
    return p === null ? 'Talk to us' : `${formatCAD(p)} / loc / mo`;
  };
  const planName = (tier: SubscriptionTier) => findPlan(plans, tier)?.name ?? tier;

  return (
    <div className="rounded-lg border border-border bg-card overflow-x-auto">
      <table className="w-full text-sm border-collapse min-w-[640px]">
        <thead>
          <tr className="border-b border-border">
            <th scope="col" className="text-left p-4 align-bottom w-2/5">
              <span className="text-xs uppercase tracking-wider text-muted-foreground">Features</span>
            </th>
            {(['standard','pro','enterprise'] as const).map((tier) => {
              const isCurrent = variant === 'billing' && currentTier === tier;
              return (
                <th
                  key={tier}
                  scope="col"
                  className="text-left p-4 align-bottom"
                  style={tier === 'pro' ? { backgroundColor: 'hsl(var(--brand-sky) / 0.10)' } : undefined}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-base font-semibold" style={{ color: 'hsl(var(--brand-navy))' }}>
                        {planName(tier)}
                      </span>
                      {isCurrent && (
                        <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                              style={{ backgroundColor: 'hsl(var(--brand-navy))', color: 'white' }}>
                          Current
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground">{priceLine(tier)}</div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {FEATURE_MATRIX.map((g) => <FeatureGroupRows key={g.group} group={g} />)}
          <tr className="border-t-2 border-border">
            <td className="p-4 align-middle text-sm text-muted-foreground">
              {variant === 'public' ? 'Get started' : 'Choose plan'}
            </td>
            <CtaCell tier="standard" variant={variant} currentTier={currentTier} />
            <CtaCell tier="pro" variant={variant} currentTier={currentTier} pro />
            <CtaCell tier="enterprise" variant={variant} currentTier={currentTier} />
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function FeatureGroupRows({ group }: { group: FeatureGroup }) {
  return (
    <>
      <tr>
        <th scope="rowgroup" colSpan={4}
          className="text-left p-3 text-xs font-semibold uppercase tracking-wider"
          style={{ backgroundColor: 'hsl(var(--brand-navy) / 0.06)', color: 'hsl(var(--brand-navy))' }}>
          {group.group}
        </th>
      </tr>
      {group.rows.map((row, i) => (
        <tr key={row.label} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
          <td className="p-3 align-top">
            <div className="text-sm">{row.label}</div>
            {row.hint && <div className="text-xs text-muted-foreground mt-0.5">{row.hint}</div>}
          </td>
          <CellTd value={row.standard} />
          <CellTd value={row.pro} pro />
          <CellTd value={row.enterprise} />
        </tr>
      ))}
    </>
  );
}

function CellTd({ value, pro = false }: { value: Cell; pro?: boolean }) {
  return (
    <td className="p-3 align-top"
      style={pro ? { backgroundColor: 'hsl(var(--brand-sky) / 0.10)' } : undefined}>
      <CellContent value={value} />
    </td>
  );
}

function CellContent({ value }: { value: Cell }) {
  if (value === true) return (
    <span aria-label="Included" title="Included">
      <span style={{ color: 'hsl(var(--brand-teal))' }} className="text-base font-semibold">✓</span>
    </span>
  );
  if (value === false) return <span aria-label="Not included" title="Not included" className="text-muted-foreground">—</span>;
  if (value === 'soon') return (
    <span className="inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
      style={{ backgroundColor: 'hsl(var(--brand-navy) / 0.10)', color: 'hsl(var(--brand-navy))' }}
      title="On the roadmap. Included when shipped.">
      Coming soon
    </span>
  );
  return <span className="text-xs">{value}</span>;
}

function CtaCell({ tier, variant, currentTier, pro = false }: {
  tier: SubscriptionTier; variant: 'public' | 'billing';
  currentTier?: SubscriptionTier | null; pro?: boolean;
}) {
  const isCurrent = variant === 'billing' && currentTier === tier;
  const isEnterprise = tier === 'enterprise';
  const proStyle = pro ? { backgroundColor: 'hsl(var(--brand-sky) / 0.10)' } : undefined;

  let inner: ReactNode;
  if (variant === 'public') {
    inner = isEnterprise ? (
      <a href={`mailto:${SALES_EMAIL}?subject=PharmaSync%20Enterprise%20inquiry`}
        className="inline-block rounded-md border-2 px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
        style={{ borderColor: 'hsl(var(--brand-navy))', color: 'hsl(var(--brand-navy))' }}>
        Contact sales
      </a>
    ) : (
      <Link to="/auth/sign-up"
        className="inline-block rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90">
        Start {TRIAL_DAYS}-day trial
      </Link>
    );
  } else {
    if (isCurrent) {
      inner = (
        <span className="inline-block rounded-md px-3 py-1.5 text-xs font-medium border"
          style={{ borderColor: 'hsl(var(--brand-navy))', color: 'hsl(var(--brand-navy))', backgroundColor: 'hsl(var(--brand-navy) / 0.06)' }}>
          Current plan
        </span>
      );
    } else if (isEnterprise) {
      inner = (
        <a href={`mailto:${SALES_EMAIL}?subject=PharmaSync%20Enterprise%20inquiry`}
          className="inline-block rounded-md border-2 px-3 py-1.5 text-xs font-medium hover:bg-muted/40"
          style={{ borderColor: 'hsl(var(--brand-navy))', color: 'hsl(var(--brand-navy))' }}>
          Contact sales
        </a>
      );
    } else {
      inner = (
        <button type="button" disabled
          className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground opacity-50 cursor-not-allowed"
          title="Self-serve plan changes will be available once payments are connected. Contact sales to switch tiers.">
          Choose plan
        </button>
      );
    }
  }
  return <td className="p-4 align-middle" style={proStyle}>{inner}</td>;
}
